import { call, put, takeLatest } from 'redux-saga/effects';
import { addToRootSaga } from 'store';
import { deleteDataFeed, getDataFeeds, validateDataFeed, updateDataFeed } from 'api/DataFeedsAPI';
import { addNegativeToast } from 'modules/toastContainer';
import { getErrorResponse } from 'utils/xhr';
import {
  DataFeedDeleteRequestAction,
  DataFeedUpdateRequestAction,
  deleteDataFeedsError,
  deleteDataFeedsResponse,
  fetchDataFeedsError,
  fetchDataFeedsResponse,
  updateDataFeedsError,
  updateDataFeedsResponse,
} from './actions';
import * as t from './actionTypes'; // see note accompanying `addToRootSaga` for import syntax variation
import { DataFeed, DataFeeds } from './types';

const message = 'Please try again later. If the problem persists, contact your CSM.';

/**
 * Delete a data feed from the API and update the Redux store.
 */
export function* deleteDataFeedSaga(action: DataFeedDeleteRequestAction) {
  try {
    yield call(deleteDataFeed, action.id);
    yield put(deleteDataFeedsResponse(action.id));
    yield call(action.cb); // close the modal
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(deleteDataFeedsError(err));
    yield put(addNegativeToast(message));
  }
}

/**
 * Retrieve the data feeds  from the API and update the Redux store.
 */
export function* fetchDataFeedsSaga() {
  try {
    const resp: { data: DataFeeds } = yield call(getDataFeeds);
    yield put(fetchDataFeedsResponse(resp.data));
  } catch (error) {
    const err = getErrorResponse(error);
    yield put(fetchDataFeedsError(err));
    yield put(addNegativeToast(message));
  }
}

/**
 * Update a data feed
 */
export function* updateDataFeedSaga(action: DataFeedUpdateRequestAction) {
  // Before any creation or edit we validate the feed and message the user if it fails
  try {
    yield call(validateDataFeed, action.payload);
  } catch (error) {
    // if the data feed validation fails we tell the user
    const err = getErrorResponse(error);
    yield put(updateDataFeedsError(err));
    return;
  }

  try {
    // unfortuantely the logic here is a little duplicated because the update also checks the validation
    // but we needed a way to reliably detect only validation errors to toggle the user assist
    const response: DataFeed | string = yield call(updateDataFeed, action.payload);
    // on NEW data feeds the API returns the data with the `id` property set (and projectId, but missing createdAt)
    // on EDIT hits the api just returns a string, so we need to use the payload
    const responseValue = (
      typeof response === 'object' && response.id ? response : action.payload
    ) as DataFeed;
    // the NEW Data Feeds response will include the id, but will be missing createdAt
    if (!responseValue.createdAt) {
      responseValue.createdAt = new Date().getTime();
    }
    yield put(updateDataFeedsResponse(responseValue));
    yield call(action.cb); // close the modal
  } catch (error) {
    yield put(updateDataFeedsError('')); // we put in an empty error in to avoid toggling a UI helper on an unknown error
    yield call(action.cb); // close the modal
    yield put(addNegativeToast(message)); // unknown errors get the toast
  }
}

addToRootSaga([
  takeLatest([t.FETCH_DATA_FEEDS_REQUEST], fetchDataFeedsSaga),
  takeLatest([t.DELETE_DATA_FEEDS_REQUEST], deleteDataFeedSaga),
  takeLatest([t.UPDATE_DATA_FEEDS_REQUEST], updateDataFeedSaga),
]);
